
import { useState } from "react";
import { Heart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useBuyer } from "@/contexts/BuyerContext";

interface BuyerPropertyDetailProps {
  property: any;
  seller?: any;
}

const BuyerPropertyDetail = ({ property, seller }: BuyerPropertyDetailProps) => {
  const { isPropertySaved, saveProperty, unsaveProperty } = useBuyer();
  const [isSaving, setIsSaving] = useState(false);
  
  const saved = isPropertySaved(property.id);

  const handleSaveToggle = async () => {
    setIsSaving(true);
    try {
      if (saved) {
        await unsaveProperty(property.id);
      } else {
        await saveProperty(property.id);
      }
    } finally {
      setIsSaving(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(amount);
  };

  return (
    <div className="space-y-6">
      {/* Action Bar - Mobile Optimized */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <Button
                variant={saved ? "default" : "outline"}
                onClick={handleSaveToggle}
                disabled={isSaving}
                className={`w-full sm:w-auto ${saved ? "bg-red-500 hover:bg-red-600" : ""}`}
                size="sm"
              >
                <Heart className={`h-4 w-4 mr-2 ${saved ? "fill-current" : ""}`} />
                {saved ? "Saved" : "Save Property"}
              </Button>
              
              <Badge variant="secondary" className="w-fit">
                Investment Opportunity
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Investment Summary */}
      {(property.arv_value || property.assignment_fee) && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Investment Details</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="text-center p-4 bg-muted rounded-lg">
                <p className="text-sm text-muted-foreground">Purchase Price</p>
                <p className="text-xl sm:text-2xl font-bold text-[#ffd700]">
                  {formatCurrency(property.price)}
                </p>
              </div>
              
              {property.arv_value && (
                <div className="text-center p-4 bg-muted rounded-lg">
                  <p className="text-sm text-muted-foreground">ARV</p>
                  <p className="text-xl sm:text-2xl font-bold">
                    {formatCurrency(property.arv_value)}
                  </p>
                </div>
              )}
              
              {property.assignment_fee && (
                <div className="text-center p-4 bg-muted rounded-lg">
                  <p className="text-sm text-muted-foreground">Assignment Fee</p>
                  <p className="text-xl sm:text-2xl font-bold">
                    {formatCurrency(property.assignment_fee)}
                  </p>
                </div>
              )}
            </div>
            
            {property.arv_value && (
              <div className="mt-4 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                <div className="flex justify-between items-center">
                  <span className="font-medium text-sm sm:text-base">Potential Equity</span>
                  <span className="text-lg sm:text-xl font-bold text-green-600">
                    {formatCurrency(property.arv_value - property.price)}
                  </span>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Next Steps */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Next Steps</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 bg-[#ffd700] text-black rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 mt-0.5">1</div>
              <span className="text-sm sm:text-base">Contact the seller to express interest and ask questions</span>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 bg-muted text-muted-foreground rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 mt-0.5">2</div>
              <span className="text-sm sm:text-base">Schedule a property viewing or virtual tour</span>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 bg-muted text-muted-foreground rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 mt-0.5">3</div>
              <span className="text-sm sm:text-base">Conduct due diligence and property analysis</span>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 bg-muted text-muted-foreground rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 mt-0.5">4</div>
              <span className="text-sm sm:text-base">Submit your offer and negotiate terms</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default BuyerPropertyDetail;
